import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withFetch,
  withInterceptorsFromDi,
} from '@angular/common/http';
import type { ApplicationConfig } from '@angular/core';
import {
  ErrorHandler,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideExperimentalZonelessChangeDetection,
} from '@angular/core';
import {
  ErrorStateMatcher,
  ShowOnDirtyErrorStateMatcher,
} from '@angular/material/core';
import {
  MAT_DIALOG_DEFAULT_OPTIONS,
  MatDialogConfig,
} from '@angular/material/dialog';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatIconRegistry } from '@angular/material/icon';
import { provideLuxonDateAdapter } from '@angular/material-luxon-adapter';
import { DomSanitizer } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
  provideRouter,
  withComponentInputBinding,
  withInMemoryScrolling,
  withRouterConfig,
} from '@angular/router';
import { ApplicationinsightsAngularpluginErrorService } from '@microsoft/applicationinsights-angularplugin-js';
import { provideTranslateService, TranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { NGX_EDITOR_CONFIG_TOKEN, NgxEditorModule } from 'ngx-editor';

import { routes } from './app.routes';
import { APP_BUILD_VERSION } from './app.version';
import { MsalInterceptor } from './core/providers/msal-intercepter';
import {
  PUBLIC_COORDINATE_RUNTIME_CONFIGURATION,
  SUPPORTED_LANGUAGES,
} from './core/providers/tokens';
import type { Configuration } from './shared/domain/configuration.model';
import type { TranslationLanguage } from './shared/domain/translations/language.model';
import { configureMatIconRegistry } from './shared/utils/configure-mat-icon-registry';

let publicCoordinateRuntimeConfiguration: Configuration;
let supportedLanguages: Map<string, TranslationLanguage>;

const loadRuntimeConfiguration = async () => {
  const response = await fetch(`/api/public/config?v=${APP_BUILD_VERSION}`);
  publicCoordinateRuntimeConfiguration =
    (await response.json()) as Configuration;
};

const loadSupportedLanguages = async () => {
  const response = await fetch(
    `/api/public/translations/languages?v=${APP_BUILD_VERSION}`,
  );
  const languages = (await response.json()) as Record<
    string,
    TranslationLanguage
  >;
  const map = new Map<string, TranslationLanguage>();
  for (const [code, language] of Object.entries(languages)) {
    map.set(code, language);
  }

  supportedLanguages = map;
};

const ngxEditorConfigFactory = (translateService: TranslateService) => {
  return {
    locals: {
      bold: translateService.stream('richTextEditor.bold'),
      italic: translateService.stream('richTextEditor.italic'),
      code: translateService.stream('richTextEditor.code'),
      underline: translateService.stream('richTextEditor.underline'),
      strike: translateService.stream('richTextEditor.strike'),
      bullet_list: translateService.stream('richTextEditor.bullet_list'),
      ordered_list: translateService.stream('richTextEditor.ordered_list'),
      heading: translateService.stream('richTextEditor.heading'),
      h1: translateService.stream('richTextEditor.h1'),
      h2: translateService.stream('richTextEditor.h2'),
      h3: translateService.stream('richTextEditor.h3'),
      h4: translateService.stream('richTextEditor.h4'),
      h5: translateService.stream('richTextEditor.h5'),
      h6: translateService.stream('richTextEditor.h6'),
      align_left: translateService.stream('richTextEditor.align_left'),
      align_center: translateService.stream('richTextEditor.align_center'),
      align_right: translateService.stream('richTextEditor.align_right'),
      align_justify: translateService.stream('richTextEditor.align_justify'),
      text_color: translateService.stream('richTextEditor.text_color'),
      format_clear: translateService.stream('richTextEditor.format_clear'),
      insertLink: translateService.stream('richTextEditor.insertLink'),
      removeLink: translateService.stream('richTextEditor.removeLink'),
      url: translateService.stream('richTextEditor.url'),
      text: translateService.stream('richTextEditor.text'),
      openInNewTab: translateService.stream('richTextEditor.openInNewTab'),
      insert: translateService.stream('richTextEditor.insert'),
      insertImage: translateService.stream('richTextEditor.insertImage'),
      altText: translateService.stream('richTextEditor.altText'),
      title: translateService.stream('richTextEditor.title'),
    },
  };
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideAppInitializer(async () => {
      configureMatIconRegistry(inject(MatIconRegistry), inject(DomSanitizer));
      await Promise.all([loadRuntimeConfiguration(), loadSupportedLanguages()]);
    }),
    provideHttpClient(withInterceptorsFromDi(), withFetch()),
    provideExperimentalZonelessChangeDetection(),
    provideRouter(
      routes,
      withComponentInputBinding(),
      withRouterConfig({
        onSameUrlNavigation: 'reload',
        paramsInheritanceStrategy: 'always',
      }),
      withInMemoryScrolling({
        scrollPositionRestoration: 'enabled',
        anchorScrolling: 'enabled',
      }),
    ),
    provideLuxonDateAdapter(),
    {
      provide: PUBLIC_COORDINATE_RUNTIME_CONFIGURATION,
      useFactory: () => publicCoordinateRuntimeConfiguration,
    },
    {
      provide: SUPPORTED_LANGUAGES,
      useFactory: () => supportedLanguages,
    },
    {
      provide: ErrorHandler,
      useClass: ApplicationinsightsAngularpluginErrorService,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    provideAnimationsAsync(),
    provideTranslateService({
      loader: provideTranslateHttpLoader({
        prefix: '/api/public/translations/i18n/',
        suffix: `.json?v=${APP_BUILD_VERSION}`,
      }),
      fallbackLang: 'en',
    }),
    importProvidersFrom(NgxEditorModule.forRoot()),
    {
      provide: NGX_EDITOR_CONFIG_TOKEN,
      useFactory: ngxEditorConfigFactory,
      deps: [TranslateService],
    },
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useFactory: () => {
        const config = new MatDialogConfig();
        // prevents visual issue where the form resets before the exit animation finishes
        config.exitAnimationDuration = '0ms';
        return config;
      },
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        subscriptSizing: 'dynamic',
      },
    },
    {
      provide: ErrorStateMatcher,
      useClass: ShowOnDirtyErrorStateMatcher,
    },
  ],
};
